import React, { useEffect, useState } from "react";
import {
  Container,
  Grid,
  Typography,
  List,
  ListItem,
  ListItemText,
  Paper,
  Box,
  styled,
  Divider,
  Avatar,
  TableCell,
  Table,
  TableHead,
  TableRow,
  TableBody,
} from "@mui/material";
import { useParams } from "react-router-dom";
import blogPosts from "../../data/caseStudies.json";
import useSEO from "../../hooks/useSEO";
import modelsData from "../../subComponents/LatestModelsandTheirPerformance/ModelsComparisonData.js";
import ModelComparison from "../../subComponents/LatestModelsandTheirPerformance/LatestModelsandTheirPerformance.js";
import ZoomableImage from "./components/zoomableImage.js";

const TOCItem = styled(ListItem)(({ theme, active }) => ({
  borderLeft: active
    ? `3px solid ${theme.palette.primary.main}`
    : "3px solid transparent",
  paddingLeft: theme.spacing(2),
  transition: "all 0.2s ease",
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

// ContentRenderer component to handle different content types
const ContentRenderer = ({ content }) => {
  if (!content) return null;

  // Error handling wrapper
  try {
    return renderContent(content);
  } catch (error) {
    console.error("Error rendering content:", error);
    // Fallback to plain text on error
    return (
      <Typography
        variant="body1"
        sx={{
          lineHeight: 1.7,
          whiteSpace: "pre-line",
        }}
      >
        {content}
      </Typography>
    );
  }
};

// Separate function for content rendering logic
const renderContent = (content) => {
  // Function to determine if content is just plain text
  const isPlainText = (text) => {
    return (
      !text.includes("<img") &&
      !text.includes("<ul") &&
      !text.includes("<ol") &&
      !text.includes("<table") &&
      !text.includes("[img:") &&
      !text.includes("[ul:") &&
      !text.includes("[ol:") &&
      !text.includes("[table:") &&
      !text.includes("[h3:") &&
      !text.toLowerCase().includes("[b:") &&
      !text.includes("[link:")
    );
  };

  // If it's simple text content, return it directly
  if (isPlainText(content)) {
    return (
      <Typography
        variant="body1"
        sx={{
          lineHeight: 1.7,
          whiteSpace: "pre-line",
        }}
      >
        {content}
      </Typography>
    );
  }

  // Handle HTML content with dangerouslySetInnerHTML
  if (
    content.includes("<img") ||
    content.includes("<ul") ||
    content.includes("<ol") ||
    content.includes("<table")
  ) {
    return (
      <Typography
        variant="body1"
        sx={{
          lineHeight: 1.7,
        }}
        dangerouslySetInnerHTML={{ __html: content }}
      />
    );
  }

  // Custom markup parsing
  const parsedElements = [];
  let currentIndex = 0;
  let key = 0;

  // Process content string to extract special content blocks
  while (currentIndex < content.length) {
    // Handle [img:URL|caption] format
    if (content.indexOf("[img:", currentIndex) === currentIndex) {
      try {
        const endIndex = content.indexOf("]", currentIndex);
        if (endIndex > 0) {
          const imgContent = content.substring(currentIndex + 5, endIndex);
          let imgSrc = imgContent;
          let imgCaption = null;

          // Handle the optional caption
          if (imgContent.includes("|")) {
            const parts = imgContent.split("|");
            imgSrc = parts[0].trim();
            imgCaption = parts.length > 1 ? parts[1].trim() : null;
          }

          // Only add the image if we have a valid source
          if (imgSrc && imgSrc.length > 0) {
            parsedElements.push(
              <Box key={`img-${key}`} sx={{ my: 3, textAlign: "center" }}>
                <Box
                  component="img"
                  src={imgSrc}
                  alt={imgCaption || "Blog image"}
                  sx={{
                    maxWidth: "100%",
                    height: "auto",
                    borderRadius: 1,
                  }}
                  onError={(e) => {
                    console.log("Image failed to load:", imgSrc);
                    e.target.onerror = null;
                    e.target.src = "/assets/placeholder-image.jpg";
                  }}
                />
                {imgCaption && (
                  <Typography
                    variant="caption"
                    sx={{
                      display: "block",
                      mt: 1,
                      color: "text.secondary",
                      color: "white",
                    }}
                  >
                    {imgCaption}
                  </Typography>
                )}
              </Box>
            );
            key++;
          }

          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering image:", error);
        // Find the next closing bracket and skip this tag
        const endIndex = content.indexOf("]", currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

   // Handle [ul:item1,item2,item3] format with nested list support
else if (content.indexOf("[ul:", currentIndex) === currentIndex) {
  try {
    const endIndex = content.indexOf("]", currentIndex);
    if (endIndex > 0) {
      const listContent = content.substring(currentIndex + 4, endIndex);
      if (listContent.trim()) {
        const items = listContent
          .split("|")
          .map((item) => item.trim())
          .filter((item) => item.length > 0);

        if (items.length > 0) {
          // Helper function to render a single list item
          const renderListItem = (item, itemKey, isNested = false) => {
            // Check if item contains nested items (marked with ^^)
            if (item.includes("^^")) {
              const parts = item.split("^^").map(part => part.trim());
              const mainItem = parts[0];
              const nestedItems = parts.slice(1).filter(part => part.length > 0);

              return (
                <ListItem
                  key={itemKey}
                  sx={{
                    display: "list-item",
                    listStyleType: isNested ? "circle" : "disc",
                    pt: 0,
                    pb: 0,
                    flexDirection: "column",
                    alignItems: "flex-start"
                  }}
                >
                  {/* Render main item */}
                  {renderItemContent(mainItem)}
                  
                  {/* Render nested list if there are nested items */}
                  {nestedItems.length > 0 && (
                    <List sx={{ 
                      paddingLeft: '10px !important', 
                      marginLeft: '16px',
                      pt: 0, 
                      pb: 0, 
                      mt: 0.5, 
                      width: "100%",
                      '& .MuiListItem-root': {
                        paddingLeft: '0px !important'
                      }
                    }}>
                      {nestedItems.map((nestedItem, nestedIndex) => 
                        renderListItem(nestedItem, `${itemKey}-nested-${nestedIndex}`, true)
                      )}
                    </List>
                  )}
                </ListItem>
              );
            } else {
              // Regular item without nesting
              return (
                <ListItem
                  key={itemKey}
                  sx={{
                    display: "list-item",
                    listStyleType: isNested ? "circle" : "disc",
                    pt: 0,
                    pb: 0,
                  }}
                >
                  {renderItemContent(item)}
                </ListItem>
              );
            }
          };

          // Helper function to render item content (with or without bold/normal split)
          const renderItemContent = (item) => {
            const dividerIndex = item.indexOf("---");
            if (dividerIndex !== -1) {
              const boldPart = item.substring(0, dividerIndex).trim();
              const normalPart = item.substring(dividerIndex + 3).trim();

              return (
                <Typography variant="body1">
                  <span style={{ fontWeight: 600 }}>{boldPart}</span>
                  {normalPart && (
                    <span style={{ fontWeight: 400 }}>
                      {" "}{normalPart}
                    </span>
                  )}
                </Typography>
              );
            } else {
              // No divider, keep original behavior (all bold)
              return (
                <Typography sx={{ fontWeight: 600 }} variant="body1">
                  {item}
                </Typography>
              );
            }
          };

          parsedElements.push(
            <List key={`ul-${key}`} sx={{ pl: 4, pt: 0, pb: 0, mt: 0 }}>
              {items.map((item, i) => renderListItem(item, i))}
            </List>
          );
          key++;
        }
      }

      currentIndex = endIndex + 1;
      continue;
    }
  } catch (error) {
    console.error("Error rendering unordered list:", error);
    const endIndex = content.indexOf("]", currentIndex);
    currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
    continue;
  }
}

    // Handle [ol:item1,item2,item3] format
    else if (content.indexOf("[ol:", currentIndex) === currentIndex) {
      try {
        const endIndex = content.indexOf("]", currentIndex);
        if (endIndex > 0) {
          const listContent = content.substring(currentIndex + 4, endIndex);
          if (listContent.trim()) {
            const items = listContent
              .split(",")
              .map((item) => item.trim())
              .filter((item) => item.length > 0);

            if (items.length > 0) {
              parsedElements.push(
                <List key={`ol-${key}`} sx={{ pl: 4, my: 2 }} component="ol">
                  {items.map((item, i) => (
                    <ListItem
                      key={i}
                      sx={{
                        display: "list-item",
                        listStyleType: "decimal",
                        py: 0.5,
                      }}
                    >
                      <Typography variant="body1">{item}</Typography>
                    </ListItem>
                  ))}
                </List>
              );
              key++;
            }
          }

          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering ordered list:", error);
        const endIndex = content.indexOf("]", currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    } else if (content.indexOf("[zoomimg:", currentIndex) === currentIndex) {
      try {
        const endIndex = content.indexOf("]", currentIndex);
        if (endIndex > 0) {
          const imgContent = content.substring(currentIndex + 9, endIndex); // +9 for '[zoomimg:'
          let imgSrc = imgContent;
          let imgCaption = null;

          // Handle the optional caption
          if (imgContent.includes("|")) {
            const parts = imgContent.split("|");
            imgSrc = parts[0].trim();
            imgCaption = parts.length > 1 ? parts[1].trim() : null;
          }

          // Only add the zoomable image if we have a valid source
          if (imgSrc && imgSrc.length > 0) {
            parsedElements.push(
              <Box key={`zoomimg-${key}`} sx={{ my: 3, textAlign: "center" }}>
                <ZoomableImage
                  src={imgSrc}
                  alt={imgCaption || "Blog zoomable image"}
                  height="600px"
                  className="mx-auto"
                />
              </Box>
            );
            key++;
          }

          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering zoomable image:", error);
        // Find the next closing bracket and skip this tag
        const endIndex = content.indexOf("]", currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // Handle [table:header1,header2|row1-col1,row1-col2|...] format
    else if (content.indexOf("[table:", currentIndex) === currentIndex) {
      try {
        const endIndex = content.indexOf("]", currentIndex);
        if (endIndex > 0) {
          const tableContent = content.substring(currentIndex + 7, endIndex);
          if (tableContent.trim()) {
            const rows = tableContent
              .split("|")
              .map((row) => row.trim())
              .filter((row) => row.length > 0);

            if (rows.length > 0) {
              const headers = rows[0]
                .split(",")
                .map((header) => header.trim())
                .filter((header) => header.length > 0);

              if (headers.length > 0) {
                parsedElements.push(
                  <Paper
                    key={`table-${key}`}
                    elevation={3}
                    sx={{
                      overflow: "hidden",
                      borderRadius: 2,
                      mt: 3,
                      mb: 4,
                      backgroundColor: "rgba(255, 255, 255, 0.05)",
                      backdropFilter: "blur(10px)",
                      color: "white",
                    }}
                  >
                    <Box sx={{ overflowX: "auto" }}>
                      <Table sx={{ minWidth: 650 }}>
                        <TableHead>
                          <TableRow
                            sx={{
                              backgroundColor: "rgba(255, 255, 255, 0.1)",
                              "& th": {
                                fontWeight: 600,
                                fontSize: "0.875rem",
                                py: 2,
                                color: "white",
                              },
                            }}
                          >
                            {headers.map((header, i) => (
                              <TableCell
                                key={i}
                                align={i === 0 ? "left" : "center"}
                              >
                                {header}
                              </TableCell>
                            ))}
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {rows.slice(1).map((row, rowIndex) => {
                            const cells = row
                              .split(",")
                              .map((cell) => cell.trim());

                            // Ensure we have the right number of cells
                            while (cells.length < headers.length) {
                              cells.push("");
                            }

                            return (
                              <TableRow
                                key={rowIndex}
                                sx={{
                                  "&:nth-of-type(odd)": {
                                    backgroundColor:
                                      "rgba(255, 255, 255, 0.03)",
                                  },
                                  "&:hover": {
                                    backgroundColor:
                                      "rgba(255, 255, 255, 0.08)",
                                    transition: "background-color 0.2s ease",
                                  },
                                }}
                              >
                                {cells.map((cell, cellIndex) => (
                                  <TableCell
                                    key={cellIndex}
                                    component={cellIndex === 0 ? "th" : "td"}
                                    scope={cellIndex === 0 ? "row" : undefined}
                                    align={cellIndex === 0 ? "left" : "center"}
                                    sx={{ color: "white" }}
                                  >
                                    {cell}
                                  </TableCell>
                                ))}
                              </TableRow>
                            );
                          })}
                        </TableBody>
                      </Table>
                    </Box>
                  </Paper>
                );
                key++;
              }
            }
          }

          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering table:", error);
        const endIndex = content.indexOf("]", currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    } else if (content.indexOf("[h3:", currentIndex) === currentIndex) {
      try {
        const endIndex = content.indexOf("]", currentIndex);
        if (endIndex > 0) {
          const headingText = content
            .substring(currentIndex + 4, endIndex)
            .trim();

          if (headingText.length > 0) {
            parsedElements.push(
              <Typography
                key={`h3-${key}`}
                variant="h3"
                component="h3"
                sx={{
                  fontWeight: 500,
                  fontSize: { xs: "1.3rem", sm: "1.55rem" },
                  mt: 4,
                  mb: 2,
                  ml: 0,
                  color: "white",
                }}
              >
                {headingText}
              </Typography>
            );
            key++;
          }

          currentIndex = endIndex + 1;
          continue;
        }
      } catch (error) {
        console.error("Error rendering h3 heading:", error);
        const endIndex = content.indexOf("]", currentIndex);
        currentIndex = endIndex > 0 ? endIndex + 1 : currentIndex + 1;
        continue;
      }
    }

    // Handle text between special tags
    const nextSpecialTagIndex = findNextSpecialTagIndex(content, currentIndex);

    if (nextSpecialTagIndex > currentIndex) {
      const textContent = content.substring(currentIndex, nextSpecialTagIndex);
      if (textContent.trim()) {
        // Process this segment (it might contain [link:] tags)
        const processedText = processInlineFormatting(textContent);
        parsedElements.push(
          <Typography
            key={`text-${key}`}
            variant="body1"
            sx={{
              lineHeight: 1.7,
              whiteSpace: "pre-line",
              my: 2,
            }}
          >
            {processedText}
          </Typography>
        );
        key++;
      }
      currentIndex = nextSpecialTagIndex;
    } else {
      // If we couldn't find any more special tags, process remaining text
      const remainingText = content.substring(currentIndex);
      if (remainingText.trim()) {
        // Process remaining text (might contain [link:] tags)
        const processedText = processInlineFormatting(remainingText);
        parsedElements.push(
          <Typography
            key={`text-${key}`}
            variant="body1"
            sx={{
              lineHeight: 1.7,
              whiteSpace: "pre-line",
              my: 2,
            }}
          >
            {processedText}
          </Typography>
        );
      }
      break; // Exit the loop as we've processed all content
    }
  }

  return <>{parsedElements}</>;
};

// Helper function to process inline links within text

const processInlineFormatting = (text) => {
  // Regular expressions to find formatting patterns
  const linkRegex = /\[link:(.*?)\|(.*?)\]/g;
  const boldRegex = /\[b:(.*?)\]/gi; // Case insensitive for [b:] or [B:]

  // If no formatting in the text, return the text as is
  if (!linkRegex.test(text) && !boldRegex.test(text)) {
    return text;
  }

  // Reset regex indices
  linkRegex.lastIndex = 0;
  boldRegex.lastIndex = 0;

  // Find all matches and their positions
  const matches = [];
  let match;

  // Find all link matches
  while ((match = linkRegex.exec(text)) !== null) {
    matches.push({
      type: "link",
      start: match.index,
      end: match.index + match[0].length,
      url: match[1].trim(),
      text: match[2].trim() || match[1].trim(),
      fullMatch: match[0],
    });
  }

  // Find all bold matches
  while ((match = boldRegex.exec(text)) !== null) {
    matches.push({
      type: "bold",
      start: match.index,
      end: match.index + match[0].length,
      text: match[1].trim(),
      fullMatch: match[0],
    });
  }

  // Sort matches by position
  matches.sort((a, b) => a.start - b.start);

  // Check for overlapping matches and remove them
  const validMatches = [];
  for (let i = 0; i < matches.length; i++) {
    const current = matches[i];
    let isValid = true;

    // Check if this match overlaps with any previously added valid match
    for (let j = 0; j < validMatches.length; j++) {
      const existing = validMatches[j];
      if (current.start < existing.end && current.end > existing.start) {
        isValid = false;
        break;
      }
    }

    if (isValid) {
      validMatches.push(current);
    }
  }

  // If no valid matches, return text as is
  if (validMatches.length === 0) {
    return text;
  }

  // Split the text into segments
  const segments = [];
  let lastIndex = 0;

  validMatches.forEach((match) => {
    // Add text before this match
    if (match.start > lastIndex) {
      segments.push({
        type: "text",
        content: text.substring(lastIndex, match.start),
      });
    }

    // Add the formatted content
    segments.push(match);

    lastIndex = match.end;
  });

  // Add any remaining text after the last match
  if (lastIndex < text.length) {
    segments.push({
      type: "text",
      content: text.substring(lastIndex),
    });
  }

  // Convert segments to React elements
  return segments.map((segment, i) => {
    if (segment.type === "text") {
      return <React.Fragment key={i}>{segment.content}</React.Fragment>;
    } else if (segment.type === "link") {
      return (
        <Box
          key={i}
          component="a"
          href={segment.url}
          target="_blank"
          rel="noopener noreferrer"
          sx={{
            color: "primary.main",
            textDecoration: "underline",
            "&:hover": {
              textDecoration: "none",
            },
          }}
        >
          {segment.text}
        </Box>
      );
    } else if (segment.type === "bold") {
      return (
        <Box
          key={i}
          component="span"
          sx={{
            fontWeight: 600,
          }}
        >
          {segment.text}
        </Box>
      );
    }
    return null;
  });
};

// Helper function to find the next special tag
const findNextSpecialTagIndex = (content, startIndex) => {
  const tagTypes = ["[img:", "[ul:", "[ol:", "[table:", "[h3:"]; // Excluded [link:] and [b:] since we handle them inline
  const indices = tagTypes.map((tag) => {
    const index = content.indexOf(tag, startIndex);
    return index >= 0 ? index : Infinity;
  });

  return Math.min(...indices);
};

const CaseStudyPostDetail = () => {
  const { slug } = useParams();
  const blogPost = blogPosts.find((post) => post.id === slug) || blogPosts[0];
  useSEO({
    title: `Blog - ${slug}- Beanbag AI`,
    description: `${blogPost.description}`,
    canonical: "https://www.beanbag.ai/",
    keywords:
      "AI, artificial intelligence, automation, business solutions, workflow optimization",

    ogTitle: "Beanbag AI",
    ogDescription: "Low-code Agentic AI Platform to Automate Workflows",
    ogImage: "https://www.beanbag.ai/images/beanbag-social-card.jpg",
    ogUrl: "https://www.beanbag.ai/",
    ogType: "website",

    twitterCard: "summary_large_image",
    twitterSite: "@BeanbagAI",

    jsonLd: {
      "@context": "https://schema.org",
      "@type": "Organization",
      name: "Beanbag AI",
      url: "https://www.beanbag.ai",
      logo: "https://www.beanbag.ai/logo192.png",
      description:
        "Beanbag AI provides intelligent automation solutions to enhance productivity and streamline workflows.",
    },
  });

  const [activeSection, setActiveSection] = useState("");

  const models = [
    {
      name: "Qwen2.5-VL-32B-Instruct",
      overall: 99,
      math: 98,
      scanned: 99,
      table: 98,
      handwritten: 99,
    },
    {
      name: "Gemini 2.5 Pro",
      overall: 98,
      math: 98,
      scanned: 97,
      table: 97,
      handwritten: 93,
    },
    {
      name: "Claude 3.7 Sonnet",
      overall: 95,
      math: 96,
      scanned: 95,
      table: 92,
      handwritten: 90,
    },
    {
      name: "GPT 4o",
      overall: 94,
      math: 97,
      scanned: 94,
      table: 93,
      handwritten: 91,
    },
    {
      name: "Mistral",
      overall: 92,
      math: 93,
      scanned: 90,
      table: 90,
      handwritten: 91,
    },
  ];

  useEffect(() => {
    const handleScroll = () => {
      const sectionElements = blogPost.sections.map((section) =>
        document.getElementById(section.id)
      );

      for (let i = sectionElements.length - 1; i >= 0; i--) {
        if (sectionElements[i]) {
          const rect = sectionElements[i].getBoundingClientRect();
          if (rect.top <= 100) {
            setActiveSection(blogPost.sections[i].id);
            break;
          }
        }
      }
    };

    window.addEventListener("scroll", handleScroll);
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [blogPost]);

  const scrollToSection = (sectionId) => {
    const element = document.getElementById(sectionId);
    if (element) {
      window.scrollTo({
        top: element.offsetTop - 80,
        behavior: "smooth",
      });
    }
  };

  return (
    <Container
      maxWidth="lg"
      sx={{
        pt: { xs: 2, sm: 3, md: 4 },
        pb: { xs: 4, sm: 6, md: 8 },
        color: "white",
        marginTop: { xs: "60px", sm: "60px", md: "80px", lg: "100px" },
      }}
    >
      <Box sx={{ mb: 6 }}>
        <Typography
          variant="h3"
          component="h1"
          gutterBottom
          sx={{
            fontWeight: 700,
            fontSize: { xs: "2rem", sm: "2.5rem", md: "3rem" },
          }}
        >
          {blogPost.title}
        </Typography>
        <Typography variant="body1" sx={{ mb: 3 }}>
          {blogPost.date} • {blogPost.readTime}
        </Typography>
        <Box
          component="img"
          src={blogPost.image}
          alt={blogPost.title}
          sx={{
            width: "100%",
            maxHeight: 400,
            objectFit: "cover",
            borderRadius: 2,
            mb: 4,
          }}
        />
      </Box>

      <Grid container spacing={4}>
        <Grid item xs={12} md={3}>
          {/* Sidebar wrapper to keep TOC and Author together */}
          <Box
            sx={{
              position: { md: "sticky" },
              top: 20,
              zIndex: 10,
            }}
          >
            {/* Table of Contents */}
            <Paper
              elevation={3}
              sx={{
                p: 2,
                backgroundColor: "#13171c",
                borderRadius: 2,
                color: "black",
                mb: 3,
              }}
            >
              <Typography
                variant="h6"
                gutterBottom
                sx={{
                  fontWeight: 600,
                  borderBottom: "1px solid #eaeaea",
                  pb: 1,
                  color: "white",
                }}
              >
                Table of Contents
              </Typography>
              <List sx={{ pl: 0 }}>
                {blogPost.sections.map((section) => (
                  <TOCItem
                    button
                    key={section.id}
                    active={activeSection === section.id ? 1 : 0}
                    onClick={() => scrollToSection(section.id)}
                    sx={{ py: 0.5 }}
                  >
                    <ListItemText
                      primary={section.title}
                      primaryTypographyProps={{
                        variant: "body2",
                        color:
                          activeSection === section.id ? "primary" : "white",
                        fontWeight: activeSection === section.id ? 600 : 400,
                      }}
                    />
                  </TOCItem>
                ))}
              </List>
            </Paper>

            {/* Author Section */}
            <Paper
              elevation={3}
              sx={{
                p: 2.5,
                backgroundColor: "transparent",
                // boxShadow: "none",
                color: "white",
                textAlign: "left",
              }}
            >
              <Typography variant="h6" gutterBottom sx={{ fontWeight: 600 }}>
                Author
              </Typography>

              <Divider
                sx={{
                  backgroundColor: "rgba(68, 68, 68, 0.8)",
                  width: "100%",
                  my: { xs: 4, sm: 5, md: 2 },
                }}
              />

              <Box
                sx={{
                  display: "flex",
                  flexDirection: "column",
                  gap: "10px",
                  alignItems: "flex-start",
                  textAlign: "left",
                }}
              >
                <Avatar
                  src={blogPost.author?.image || "/author-placeholder.jpg"}
                  alt={blogPost.author?.name || "Author"}
                  sx={{
                    width: 60,
                    height: 60,
                    mr: 2,
                    border: "2px solid #eaeaea",
                  }}
                />

                <Box>
                  <Typography
                    variant="subtitle1"
                    sx={{
                      fontWeight: 600,
                      mb: 0.5,
                    }}
                  >
                    {blogPost.author?.name || "Akash Ambade"}
                  </Typography>

                  <Typography
                    variant="body2"
                    // color="text.secondary"
                    sx={{ mb: 1 }}
                  >
                    {blogPost.author?.designation || "Product Owner"}
                  </Typography>

                  {blogPost.author?.bio && (
                    <Typography
                      variant="body2"
                      color="text.secondary"
                      sx={{
                        mt: 1,
                        fontStyle: "italic",
                        textAlign: "left",
                      }}
                    >
                      {blogPost.author.bio}
                    </Typography>
                  )}
                </Box>
              </Box>
            </Paper>
          </Box>
        </Grid>

        <Grid item xs={12} md={9}>
          {blogPost.sections.map((section) => (
            <Box
              key={section.id}
              id={section.id}
              sx={{
                mb: 4,
                scrollMarginTop: "80px",
              }}
            >
              <Typography
                variant="h4"
                component="h2"
                gutterBottom
                sx={{
                  fontWeight: 700,
                  mb: 2,
                  fontSize: {
                    xs: "1.75rem",
                    md: "2rem",
                  },
                }}
              >
                {section.title}
              </Typography>
              <ContentRenderer content={section.content} />

              {/* For the models performance section, use ModelComparison component */}
              {section.id === "latest-models-and-performance" && (
                <Box
                  sx={{
                    mb: 4,
                    mt: 2,
                  }}
                >
                  <ModelComparison models={modelsData} />
                </Box>
              )}
            </Box>
          ))}
        </Grid>
      </Grid>
    </Container>
  );
};

export default CaseStudyPostDetail;
